import { isNotNil } from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import {
	EgnyteGlobalSearchItemPersistedModel,
	GlobalSearchItemPersistedModel
} from "./models";
import {
	GlobalSearchResultItemKind,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { logNever } from "client_legacy/ndaw/errors";

export function areTheSame(item1: GlobalSearchItemPersistedModel, item2: GlobalSearchItemPersistedModel): boolean {
	if (item1.kind !== item2.kind) {
		return false;
	}

	if (!areScopesTheSame(item1.itemScope, item2.itemScope)) {
		return false;
	}

	switch (item1.kind) {
		case GlobalSearchResultItemKind.App:
			return compareData(
				x => x.app,
				(a1, a2) => a1.id === a2.id
			);
		case GlobalSearchResultItemKind.Announcement:
			return compareData(
				x => x.announcement,
				(a1, a2) => a1.id === a2.id
			);
		case GlobalSearchResultItemKind.Community:
			return compareData(
				x => x.community,
				(c1, c2) => c1.id === c2.id
			);
		case GlobalSearchResultItemKind.CommunityPost:
			return compareData(
				x => x.communityPost,
				(p1, p2) => p1.id === p2.id
			);
		case GlobalSearchResultItemKind.Contact:
			return compareData(
				x => x.contact,
				(c1, c2) => c1.id === c2.id
			);
		case GlobalSearchResultItemKind.Document:
			return compareData(
				x => x.document,
				(d1, d2) => d1.location.siteUrl === d2.location.siteUrl
					&& d1.location.listId === d2.location.listId
					&& d1.id === d2.id
			);
		case GlobalSearchResultItemKind.EgnyteItem:
			return compareData(
				x => x.egnyteItem,
				areEgnyteResultsTheSame
			);
		case GlobalSearchResultItemKind.Email:
			return compareData(
				x => x.email,
				(e1, e2) => e1.emailId === e2.emailId
			);
		case GlobalSearchResultItemKind.Event:
			return compareData(
				x => x.event,
				(e1, e2) => e1.id === e2.id
			);
		case GlobalSearchResultItemKind.IntegrationFlow:
			return compareData(
				x => x.integrationFlow,
				deepComparison
			);
		case GlobalSearchResultItemKind.KnowledgeItems:
			return compareData(
				x => x.knowledgeItem,
				(i1, i2) => i1.id === i2.id
			);
		default:
			logNever(item1.kind);
			return false;
	}

	function compareData<TData>(
		dataSelect: (model: GlobalSearchItemPersistedModel) => TData | undefined,
		areEqual: ((data1: TData, data2: TData) => boolean)
	): boolean {
		const data1: TData | undefined = dataSelect(item1);
		const data2: TData | undefined = dataSelect(item2);

		return isNotNil(data1)
			&& isNotNil(data2)
			&& areEqual(data1, data2);
	}
}

function areScopesTheSame(scope1: GlobalSearchScopeIdentifier, scope2: GlobalSearchScopeIdentifier): boolean {
	switch (scope1.kind) {
		case GlobalSearchScopeKind.Predefined:
			switch (scope2.kind) {
				case GlobalSearchScopeKind.Predefined:
					return scope1.predefinedKind === scope2.predefinedKind;
				case GlobalSearchScopeKind.IntegrationFlow:
					return false;
				default:
					logNever(scope2);
					return false;
			}
		case GlobalSearchScopeKind.IntegrationFlow:
			switch (scope2.kind) {
				case GlobalSearchScopeKind.Predefined:
					return false;
				case GlobalSearchScopeKind.IntegrationFlow:
					return scope1.integrationId === scope2.integrationId;
				default:
					logNever(scope2);
					return false;
			}
		default:
			logNever(scope1);
			return false;
	}
}

function areEgnyteResultsTheSame(egnyte1: EgnyteGlobalSearchItemPersistedModel, egnyte2: EgnyteGlobalSearchItemPersistedModel): boolean {
	return egnyte1.url === egnyte2.url
		&& egnyte1.path === egnyte2.path
		&& egnyte1.name === egnyte2.name
		&& egnyte1.extension === egnyte2.extension;
}
